import React, { useState, useEffect } from 'react';
import { FaChevronUp } from 'react-icons/fa';
import { useLanguage } from '../pages/LanguageContext';

import Energy from '../images/symbole-d-eclair-vert.png';
import Eco from '../images/eco-friendlygreen-leaf-icon.png';
import Waste from '../images/icone-de-recyclage-et-de-durabilite-vert.png';

const translations = {
  en: {
    heading: "🌱 Our Commitment to the Environment Smarter technology. Greener choices. A better tomorrow",
    paragraph: "At Mass Smart Solutions, sustainability isn't an add-on. It's embedded in everything we do. We design smart spaces that don’t just think for you; they also think for the planet. From cutting energy waste to minimizing installation impact, every decision we make aims to protect resources, reduce environmental harm, and promote long-term well-being for people and planet alike.",
    features: [
      {
        icon: Energy,
        alt: 'Energy Saving',
        title: "Energy Efficiency",
        text: "Our automation systems are built to optimize energy consumption across lighting, HVAC, and appliances. Motion sensors, intelligent scheduling, and adaptive routines ensure energy is only used when and where it’s needed. Clients have reported up to 30% reductions in electricity use simply by shifting to smart controls, without sacrificing comfort or functionality.",
      },
      {
        icon: Eco,
        alt: 'Sustainable Materials',
        title: "Eco-Friendly Materials",
        text: "We carefully select components that meet international standards for sustainability, including RoHS-compliant and low-emission materials. From recyclable switch panels to modular smart hubs, our product sourcing prioritizes long-term durability and reduced environmental impact. Even packaging is optimized for minimal waste and easy recycling.",
      },
      {
        icon: Waste,
        alt: 'Waste Reduction',
        title: "Minimized Waste",
        text: "Our installation process is designed to preserve walls, surfaces, and finishes, avoiding unnecessary drilling or wiring whenever possible. With wireless solutions like Zigbee and Matter devices, we eliminate the need for heavy infrastructure, reducing debris and material use. Every site is left cleaner than we found it, and our remote support reduces unnecessary service visits, lowering emissions over time.",
      }
    ]
  },
  ar: {
    heading: "🌱 التزامنا تجاه البيئة تقنية أذكى. خيارات أكثر إخضراراً. من أجل غدٍ أفضل",
    paragraph: "في Mass Smart Solutions، لا تُعد الاستدامة ميزة إضافية، بل هي جزء أساسي من كل ما نقوم به. نصمّم مساحات ذكية لا تفكّر فقط بك، بل تفكّر أيضًا بكوكبنا. بدءاً من تقليل الهدر في استهلاك الطاقة، وصولًا إلى تقليل الأثر البيئي أثناء التركيب، كل قرار نتخذه يهدف إلى الحفاظ على الموارد وتقليل الأضرار البيئية وتعزيز رفاهية الإنسان على المدى الطويل.",
    features: [
      {
        icon: Energy,
        alt: 'توفير الطاقة',
        title: "كفاءة الطاقة",
        text: "نقوم ببناء أنظمتنا الذكية لتُحسّن استهلاك الطاقة في الإضاءة، وأنظمة التكييف، والأجهزة الكهربائية. من خلال مستشعرات الحركة، والجداول الذكية، والروتينات التكيفية، نضمن استخدام الطاقة فقط عند الحاجة وفي الوقت المناسب. وقد سجّل عملاؤنا انخفاضاً في استهلاك الكهرباء بنسبة تصل إلى 30٪ بعد اعتماد أنظمة التحكم الذكية، دون أي تنازل عن الراحة أو الأداء.",
      },
      {
        icon: Eco,
        alt: 'مواد صديقة للبيئة',
        title: "مواد صديقة للبيئة",
        text: "نختار مكوناتنا بعناية لتتوافق مع المعايير العالمية للاستدامة، بما في ذلك مواد منخفضة الانبعاثات ومتوافقة مع مواصفات RoHS. من الألواح القابلة لإعادة التدوير إلى الوحدات الذكية القابلة للتوسعة، نُعطي الأولوية للمتانة طويلة الأمد وتقليل التأثير البيئي. حتى التغليف نعمل على تقليله ليكون قابلاً لإعادة التدوير وسهل التخلص منه.",
      },
      {
        icon: Waste,
        alt: 'تقليل النفايات',
        title: "تقليل الهدر",
        text: "تصميم عملية التركيب لدينا يراعي الحفاظ على الجدران والتشطيبات دون الحاجة إلى الحفر أو تمديد الأسلاك غير الضروري. من خلال اعتماد حلول لاسلكية مثل Zigbee وWi-Fi، نتجنب البنية التحتية المعقدة، ونُقلّل من المواد المستخدمة والمخلّفات. نغادر كل موقع نظيفًا كما وجدناه، ونوفر دعمًا عن بُعد يُقلّل من زيارات الصيانة الميدانية والانبعاثات المصاحبة لها.",
      }
    ]
  }
};

const WindTurbine = ({ size = 220 }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 400"
      width={size}
      height={size * 1.6}
      style={{
        cursor: 'pointer',
        transition: 'filter 0.3s ease',
        filter: isHovered
          ? 'drop-shadow(0 0 12px #66bb6a) drop-shadow(0 0 18px #a5d6a7)'
          : 'drop-shadow(0 6px 6px rgba(0,0,0,0.15))',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <defs>
        <linearGradient id="towerGradient" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2e7d32" />
          <stop offset="100%" stopColor="#145214" />
        </linearGradient>
        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#a5d6a7" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#388e3c" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="90" y="150" width="20" height="230" fill="url(#towerGradient)" rx="4" ry="4" />
      <rect x="60" y="380" width="80" height="15" fill="#1b5e20" rx="8" ry="8" />
      <circle cx="100" cy="140" r="18" fill="#66bb6a" />
      <circle cx="100" cy="140" r="30" fill="url(#hubGlow)" />
      <g
        style={{
          transformOrigin: '100px 140px',
          animation: `${isHovered ? 'blade-spin-fast' : 'blade-spin'} 6s linear infinite`,
        }}
      >
        <rect x="95" y="20" width="10" height="100" rx="5" ry="5" fill="#81c784" />
        <rect x="95" y="40" width="10" height="100" rx="5" ry="5" fill="#81c784" transform="rotate(120 100 140)" />
        <rect x="95" y="40" width="10" height="100" rx="5" ry="5" fill="#81c784" transform="rotate(240 100 140)" />
      </g>
      <style>{`
        @keyframes blade-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes blade-spin-fast {
          from { transform: rotate(0deg); }
          to { transform: rotate(720deg); }
        }
      `}</style>
    </svg>
  );
};

export default function EnvironmentalRole() {
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === "ar";

  const [showButton, setShowButton] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  useEffect(() => {
    document.title = isAr
      ? 'المسؤولية البيئية | ماس سمارت سوليوشن'
      : 'Environmental Responsibility | We Mass Smart Solution';

    const handleScroll = () => setShowButton(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAr]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <main style={{
      direction: isAr ? 'rtl' : 'ltr',
      fontFamily: isAr ? "'Cairo', sans-serif" : "'Poppins', sans-serif",
      background: 'linear-gradient(145deg, #e8f5e9, #c8e6c9)',
      padding: '2rem',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{ position: 'absolute', bottom: 0, left: 0, opacity: 0.12 }} className="hide-mobile">
        <WindTurbine />
      </div>
      <div style={{ position: 'absolute', bottom: 0, right: 0, opacity: 0.12 }} className="hide-mobile">
        <WindTurbine />
      </div>

      <h1 style={{
        fontSize: '2.4rem',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '1rem',
        background: 'linear-gradient(to right, #2e7d32, #81c784)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        lineHeight: '1.4',
        padding: '0 1rem',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}>{t.heading}</h1>

      <p style={{
        maxWidth: '850px',
        fontSize: '1.1rem',
        lineHeight: 1.7,
        color: '#1b5e20',
        marginBottom: '2rem',
        textAlign: 'center',
      }}>{t.paragraph}</p>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '2rem',
        justifyContent: 'center',
      }}>
        {t.features.map((feature, i) => (
          <div key={i} className="eco-card" style={{
            background: 'white',
            border: '2px solid #a5d6a7',
            borderRadius: '16px',
            padding: '1.5rem',
            maxWidth: '280px',
            textAlign: 'center',
            boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
            transition: 'transform 0.3s ease',
          }}>
            <img src={feature.icon} alt={feature.alt} style={{ width: '70px', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.4rem', color: '#2e7d32', marginBottom: '0.5rem' }}>{feature.title}</h3>
            <p style={{ fontSize: '1rem', color: '#388e3c' }}>
              {expandedIndex === i ? feature.text : `${feature.text.slice(0, 110)}...`}
            </p>
            <button
              onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
              style={{
                background: 'none',
                color: '#1b5e20',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginTop: '0.5rem',
              }}
            >
              {expandedIndex === i ? (isAr ? 'عرض أقل' : 'Show Less') : (isAr ? 'اقرأ المزيد' : 'Read More')}
            </button>
          </div>
        ))}
      </div>

      <div style={{
        background: '#f1f8e9',
        marginTop: '3rem',
        padding: '2rem',
        borderRadius: '16px',
        textAlign: 'center',
        maxWidth: '850px',
        width: '100%',
        boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
      }}>
        <p style={{ fontSize: '1.2rem', color: '#2e7d32', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
          {isAr
            ? 'هل ترغب بالتحوّل إلى حلول أكثر استدامة دون التضحية بالتكنولوجيا؟\nدع Mass Smart Solutions تساعدك...'
            : 'Ready to go green without compromising on innovation?\nLet Mass Smart Solutions help you...'}
        </p>
        <a href="/contact" style={{
  display: 'inline-block',
  marginTop: '1rem',
  padding: '14px 28px',
  backgroundColor: '#00A86B',
  color: 'white',
  borderRadius: '12px',
  textDecoration: 'none',
  fontWeight: 'bold',
  fontSize: '1.05rem',
  transition: 'background 0.3s ease',
}} onMouseEnter={e => e.target.style.backgroundColor = '#007A53'}
   onMouseLeave={e => e.target.style.backgroundColor = '#00A86B'}>
   {isAr ? '👉تواصل معنا اليوم وابدأ مستقبلًا أكثر ذكاءً وبيئية' : '👉 Contact us today and start building your eco-smart future'}
</a>

      </div>

      {showButton && (
        <button onClick={scrollToTop} style={{
          position: 'fixed',
          bottom: '30px',
          left: '30px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          backgroundColor: '#1b5e20',
          color: '#fff',
          border: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          cursor: 'pointer',
        }}>
          <FaChevronUp />
        </button>
      )}

      <style>{`
        .eco-card:hover {
          transform: translateY(-5px);
        }

        .hide-mobile {
          display: block;
        }

        @media (max-width: 768px) {
         main h1 {
            font-size: 1.2rem !important;
            white-space: normal !important;
            line-height: 1.4 !important;
            padding: 0 1rem !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            text-align: center !important;
          }

         

          .eco-card {
            width: 100% !important;
            max-width: 100% !important;
          }

          .eco-card img {
            width: 60px !important;
          }

          .eco-card h3 {
            font-size: 1.2rem !important;
          }

          .eco-card p {
            font-size: 0.95rem !important;
          }

          .eco-card button {
            font-size: 0.9rem !important;
          }

          .hide-mobile {
            display: none !important;
          }
        }
      `}</style>
    </main>
  );
}
